import { memo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatRt } from "@/utils/formatRt";

/** RT 축 위의 시리즈 하나 (TIC 겹쳐보기·EIP 이온 등). */
export interface RtSeries {
  key: string;
  label: string;
  color: string;
  /** CVD·인쇄 대비 보조 부호화 — 점선 패턴 (선택). */
  strokeDasharray?: string;
  points: { rt: number; intensity: number }[];
}

interface RtOverlayChartProps {
  series: RtSeries[];
  height?: number;
  /** 시리즈가 하나도 없거나 전부 빈 경우 대신 보여줄 안내문. */
  emptyMessage?: string;
  ariaLabel?: string;
}

/**
 * RT(머무름시간) 축 다중 시리즈 라인 차트 — T3 화재잔해 클래스 모드.
 *
 * 그리기만 한다. 시리즈 간 비교·패턴 판단(ASTM 시각적 패턴 매칭)은
 * 감정인의 영역이다. 각 시리즈는 자기 data 를 갖는다 — RT 격자가 서로
 * 달라도(시료 vs 대조군) 병합 없이 겹쳐 그린다.
 */
const RtOverlayChart = ({
  series,
  height = 300,
  emptyMessage = "표시할 데이터가 없습니다.",
  ariaLabel = "RT 다중 시리즈 차트",
}: RtOverlayChartProps) => {
  const drawable = series.filter((s) => s.points.length > 0);

  if (drawable.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-gray-500"
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div role="img" aria-label={ariaLabel}>
      <ResponsiveContainer debounce={0} width="100%" height={height}>
        <LineChart margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis
            dataKey="rt"
            type="number"
            domain={["dataMin", "dataMax"]}
            tick={{ fill: "#64748b", fontSize: 12 }}
            label={{
              value: "RT (min)",
              position: "insideBottomRight",
              offset: -4,
              fill: "#64748b",
              fontSize: 12,
            }}
            allowDuplicatedCategory={false}
          />
          <YAxis
            tick={{ fill: "#64748b", fontSize: 12 }}
            label={{
              value: "Intensity",
              angle: -90,
              position: "insideLeft",
              fill: "#64748b",
              fontSize: 12,
            }}
            width={70}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              fontSize: 12,
            }}
            labelFormatter={(label) => `RT ${formatRt(Number(label))} min`}
            formatter={(value, name) => [
              Number(value).toLocaleString(),
              String(name),
            ]}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {drawable.map((s) => (
            <Line
              key={s.key}
              data={s.points}
              dataKey="intensity"
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              strokeDasharray={s.strokeDasharray}
              dot={false}
              isAnimationActive={false}
              type="linear"
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default memo(RtOverlayChart);
